import { useState, useRef, useCallback } from "react";

const W = 1280;
const H = 720;

const FONTS = [
  { label: "Georgia — Editorial Serif", value: "Georgia,'Times New Roman',serif" },
  { label: "Palatino — Elegant Serif", value: "'Palatino Linotype',Palatino,'Book Antiqua',serif" },
  { label: "Times — Classic Serif", value: "'Times New Roman',Times,serif" },
  { label: "Garamond — Refined Serif", value: "Garamond,'EB Garamond',serif" },
  { label: "Arial Black — Punchy Sans", value: "'Arial Black',Gadget,sans-serif" },
  { label: "Impact — Tabloid Bold", value: "Impact,'Haettenschweiler','Arial Narrow Bold',sans-serif" },
  { label: "Helvetica — Clean Sans", value: "'Helvetica Neue',Helvetica,Arial,sans-serif" },
  { label: "Verdana — Wide Sans", value: "Verdana,Geneva,sans-serif" },
  { label: "Trebuchet — Friendly Sans", value: "'Trebuchet MS',sans-serif" },
  { label: "Courier — Mono Slab", value: "'Courier New',Courier,monospace" },
];

const HEADLINE_COLORS = [
  { label: "Ivory", value: "#F5F0E8" },
  { label: "White", value: "#FFFFFF" },
  { label: "Gold", value: "#C4943D" },
  { label: "Steel", value: "#6B7F8E" },
];

const PRESETS = [
  { label: "Script 1", headline: "SHE ALREADY *LEFT*", sub: "The Silent Divorce" },
  { label: "Script 2", headline: "THE *SILENCE* BEFORE", sub: "Why Wives Withdraw" },
  { label: "Script 3", headline: "SHE STOPPED *FIGHTING*", sub: "She Already Decided" },
];

const OVERLAYS = [
  { label: "Right fade", dir: "right" },
  { label: "Left fade", dir: "left" },
  { label: "Bottom up", dir: "top" },
  { label: "Full dark", dir: "full" },
];

function hexRgb(hex) {
  if (!hex || hex.length < 7) return "15,28,46";
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ].join(",");
}

// Parse "WORD *HIGHLIGHTED* WORD" into tokens with color flags.
// Returns array of { text, highlight } tokens (one per word).
function parseTokens(raw) {
  if (!raw) return [];
  const text = raw.toUpperCase();
  const tokens = [];
  // Split on whitespace, but track asterisk markers per word
  const words = text.split(/\s+/).filter(Boolean);
  let inHL = false;
  for (let w of words) {
    let highlight = inHL;
    // Handle asterisks at start
    while (w.startsWith("*")) {
      inHL = !inHL;
      highlight = inHL || highlight; // word touched by toggle becomes highlighted
      w = w.slice(1);
    }
    // Track trailing asterisks but apply highlight to this word first
    let trailingToggle = 0;
    while (w.endsWith("*")) {
      trailingToggle++;
      w = w.slice(0, -1);
    }
    if (w) {
      // If we opened on this word OR we're inside an open span, highlight it
      tokens.push({ text: w, highlight: inHL || highlight });
    }
    // Apply trailing toggles AFTER the word
    for (let i = 0; i < trailingToggle; i++) inHL = !inHL;
  }
  return tokens;
}

function CompassMark({ color = "#6B7F8E", size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3 A9 9 0 1 1 11.99 3"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeDasharray="50 6"
        strokeDashoffset="-3"
        fill="none"
      />
      <line x1="12" y1="3" x2="12" y2="21" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="12" cy="12" r="1.8" fill={color} />
    </svg>
  );
}

function Label({ text }) {
  return (
    <p style={{
      fontSize: 10, color: "#4A6070", letterSpacing: "0.18em",
      textTransform: "uppercase", fontFamily: "sans-serif", marginBottom: 7,
    }}>
      {text}
    </p>
  );
}

function Swatch({ color, active, onClick }) {
  return (
    <button
      onClick={onClick}
      title={color}
      style={{
        width: 28, height: 28, background: color, flexShrink: 0, cursor: "pointer",
        border: active ? "2px solid #6B7F8E" : "1px solid #2A3D52", borderRadius: 4,
      }}
    />
  );
}

function PillBtn({ active, onClick, children, small = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: small ? "4px 8px" : "6px 0",
        fontSize: small ? 11 : 12, cursor: "pointer",
        background: active ? "#6B7F8E" : "#071220",
        border: `1px solid ${active ? "#6B7F8E" : "#2A3D52"}`,
        borderRadius: 4,
        color: active ? "#0A0F1A" : "#6B7F8E",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function drawCompass(ctx, x, y, size, color) {
  const r = size * 0.38;
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = color;
  ctx.lineWidth = size * 0.05;
  ctx.lineCap = "round";
  ctx.setLineDash([size * 2.1, size * 0.25]);
  ctx.lineDashOffset = -size * 0.13;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineWidth = size * 0.06;
  ctx.beginPath();
  ctx.moveTo(0, -r - size * 0.04);
  ctx.lineTo(0, r + size * 0.04);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.075, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export default function ThumbnailEditor() {
  const [headline, setHeadline] = useState("SHE ALREADY *LEFT*");
  const [sub, setSub] = useState("The Silent Divorce");
  const [channel, setChannel] = useState("Still Point Institute");
  const [img, setImg] = useState("");
  const [imgObj, setImgObj] = useState(null);
  const [hColor, setHColor] = useState("#F5F0E8");
  const [font, setFont] = useState(FONTS[0].value);
  const [pos, setPos] = useState("right");
  const [opacity, setOpacity] = useState(45);
  const [overlayDir, setOverlayDir] = useState("right");
  const [hSize, setHSize] = useState(52);
  const [lineSpacing, setLineSpacing] = useState(1.08);
  const [showLogo, setShowLogo] = useState(true);
  const [bg, setBg] = useState("#0F1C2E");
  const [accent, setAccent] = useState("#6B7F8E");
  const [highlightColor, setHighlightColor] = useState("#C4943D");
  const [downloading, setDownloading] = useState(false);

  const fileRef = useRef();
  const canvasRef = useRef();

  const loadFile = useCallback((file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = (e) => {
      setImg(e.target.result);
      const image = new Image();
      image.onload = () => setImgObj(image);
      image.src = e.target.result;
    };
    r.readAsDataURL(file);
  }, []);

  const renderToCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Background
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Scene image — cover fill
    if (imgObj) {
      const iw = imgObj.naturalWidth, ih = imgObj.naturalHeight;
      const scale = Math.max(W / iw, H / ih);
      const sw = iw * scale, sh = ih * scale;
      const sx = (W - sw) / 2, sy = (H - sh) / 2;
      ctx.drawImage(imgObj, sx, sy, sw, sh);
    }

    // Overlay gradient
    const rgb = hexRgb(bg);
    const a1 = opacity / 100;
    const a2 = Math.max(0, a1 - 0.3);
    let grad;
    if (overlayDir === "right") {
      grad = ctx.createLinearGradient(0, 0, W, 0);
    } else if (overlayDir === "left") {
      grad = ctx.createLinearGradient(W, 0, 0, 0);
    } else if (overlayDir === "top") {
      grad = ctx.createLinearGradient(0, H, 0, 0);
    } else {
      ctx.fillStyle = `rgba(${rgb},${a1})`;
      ctx.fillRect(0, 0, W, H);
      grad = null;
    }
    if (grad) {
      grad.addColorStop(0, `rgba(${rgb},${a1})`);
      grad.addColorStop(0.45, `rgba(${rgb},${a1})`);
      grad.addColorStop(1, `rgba(${rgb},${a2})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
    }

    // Scale headline size to canvas resolution (preview uses ~680px wide, canvas is 1280px)
    const scaledSize = Math.round(hSize * (W / 680));
    const textW = W * 0.56;
    const marginX = W * 0.04;

    let textAnchorX;
    if (pos === "right") textAnchorX = W - marginX;
    else if (pos === "left") textAnchorX = marginX;
    else textAnchorX = W / 2;

    const textAlign = pos === "right" ? "right" : pos === "left" ? "left" : "center";

    // Tokenize headline (preserving highlight flags), then wrap into lines.
    ctx.font = `700 ${scaledSize}px ${font}`;
    const tokens = parseTokens(headline || "YOUR HEADLINE");
    const spaceW = ctx.measureText(" ").width;

    // Measure each token; we'll wrap based on line width budget.
    const measured = tokens.map((t) => ({ ...t, w: ctx.measureText(t.text).width }));

    const lines = []; // each line = array of { text, highlight, w }
    let currentLine = [];
    let currentW = 0;
    for (const tok of measured) {
      const addW = currentLine.length === 0 ? tok.w : tok.w + spaceW;
      if (currentLine.length > 0 && currentW + addW > textW - 20) {
        lines.push(currentLine);
        currentLine = [tok];
        currentW = tok.w;
      } else {
        currentLine.push(tok);
        currentW += addW;
      }
    }
    if (currentLine.length) lines.push(currentLine);

    const lineH = scaledSize * lineSpacing;
    const subSize = Math.round(scaledSize * 0.3);
    const chanSize = Math.round(scaledSize * 0.2);
    const totalH =
      lines.length * lineH +
      16 +
      (sub ? subSize + 10 : 0) +
      chanSize + 20;

    let curY = (H - totalH) / 2 + scaledSize * 0.85;

    // Draw headline lines token-by-token to support per-word color
    ctx.shadowColor = "rgba(0,0,0,0.7)";
    ctx.shadowBlur = 18;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    lines.forEach((line, li) => {
      // Total line width (with spaces)
      const lineWidth = line.reduce((acc, t, i) => acc + t.w + (i > 0 ? spaceW : 0), 0);

      // Compute starting X based on alignment
      let startX;
      if (textAlign === "right") startX = textAnchorX - lineWidth;
      else if (textAlign === "center") startX = textAnchorX - lineWidth / 2;
      else startX = textAnchorX;

      ctx.textAlign = "left"; // we manage positioning manually
      let x = startX;
      const y = curY + li * lineH;
      line.forEach((tok, i) => {
        if (i > 0) x += spaceW;
        ctx.fillStyle = tok.highlight ? highlightColor : hColor;
        ctx.fillText(tok.text, x, y);
        x += tok.w;
      });
    });

    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.textAlign = textAlign;
    curY += lines.length * lineH + 14;

    // Accent bar
    const barW = 54;
    const barX =
      pos === "right" ? textAnchorX - barW :
      pos === "left" ? textAnchorX :
      textAnchorX - barW / 2;
    ctx.fillStyle = accent;
    ctx.fillRect(barX, curY, barW, 4);
    curY += 18;

    // Subline
    if (sub) {
      ctx.font = `italic 400 ${subSize}px ${font}`;
      ctx.fillStyle = "#C8C4BC";
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 8;
      ctx.fillText(sub, textAnchorX, curY + subSize * 0.85);
      ctx.shadowBlur = 0;
      curY += subSize + 10;
    }

    // Channel name
    ctx.font = `italic 400 ${chanSize}px ${font}`;
    ctx.fillStyle = "#8BA0AF";
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = 6;
    ctx.fillText(channel.toUpperCase(), textAnchorX, curY + chanSize * 0.85);
    ctx.shadowBlur = 0;

    // Compass logo
    if (showLogo) {
      drawCompass(ctx, 38, H - 38, 30, accent);
    }

    return canvas;
  }, [headline, sub, channel, imgObj, hColor, font, pos, opacity, overlayDir, hSize, lineSpacing, showLogo, bg, accent, highlightColor]);

  const handleDownload = useCallback(() => {
    setDownloading(true);
    setTimeout(() => {
      try {
        const canvas = renderToCanvas();
        if (!canvas) return;
        const link = document.createElement("a");
        const slug = (headline || "thumbnail")
          .replace(/\*/g, "")
          .toLowerCase().replace(/\s+/g, "-").slice(0, 30);
        link.download = `still-point-${slug}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      } finally {
        setDownloading(false);
      }
    }, 50);
  }, [renderToCanvas, headline]);

  // Preview overlay gradient CSS
  const rgb = hexRgb(bg);
  const a1 = opacity / 100;
  const a2 = Math.max(0, a1 - 0.3);
  const gradMap = {
    right: `linear-gradient(to right, rgba(${rgb},${a1}) 30%, rgba(${rgb},${a2}) 100%)`,
    left: `linear-gradient(to left, rgba(${rgb},${a1}) 30%, rgba(${rgb},${a2}) 100%)`,
    top: `linear-gradient(to top, rgba(${rgb},${a1}) 30%, rgba(${rgb},${a2}) 100%)`,
    full: `rgba(${rgb},${a1})`,
  };

  const textContainerStyle = {
    position: "absolute", top: "50%", width: "58%", pointerEvents: "none", zIndex: 10,
    ...(pos === "right"
      ? { right: "4%", textAlign: "right", transform: "translateY(-50%)" }
      : pos === "left"
      ? { left: "4%", textAlign: "left", transform: "translateY(-50%)" }
      : { left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", width: "72%" }),
  };

  const accentBarStyle = {
    width: 36, height: 2, background: accent, marginBottom: 8,
    ...(pos === "right" ? { marginLeft: "auto" }
      : pos === "center" ? { margin: "0 auto 8px" } : {}),
  };

  // Tokens for live preview rendering
  const previewTokens = parseTokens(headline || "YOUR HEADLINE");

  return (
    <div style={{ minHeight: "100vh", background: "#0A0F1A", color: "#E8E4DC", fontFamily: "Georgia, serif", padding: "1.5rem" }}>
      <canvas ref={canvasRef} style={{ display: "none" }} />

      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "1.75rem", paddingBottom: "1.25rem", borderBottom: "1px solid #1E2D3D" }}>
          <CompassMark color={accent} size={28} />
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 400, letterSpacing: "0.15em", color: "#F5F0E8", margin: 0 }}>
              STILL POINT INSTITUTE
            </h1>
            <p style={{ fontSize: 11, color: "#4A6070", letterSpacing: "0.2em", margin: 0 }}>
              THUMBNAIL EDITOR
            </p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "1.75rem", alignItems: "start" }}>

          {/* Controls Panel */}
          <div style={{ background: "#0F1C2E", border: "1px solid #1E2D3D", borderRadius: 8, padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1.1rem" }}>

            {/* Image Upload */}
            <div>
              <Label text="Oil Painting" />
              <div
                onDrop={(e) => { e.preventDefault(); loadFile(e.dataTransfer.files[0]); }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileRef.current.click()}
                style={{ border: "1px dashed #2A3D52", borderRadius: 6, padding: "1rem", textAlign: "center", cursor: "pointer", background: "#071220" }}
              >
                <div style={{ fontSize: 22, marginBottom: 4 }}>{img ? "✓" : "🖼"}</div>
                <p style={{ margin: 0, fontSize: 11, color: img ? "#3B7A57" : "#4A6070" }}>
                  {img ? "Loaded — click to replace" : "Drop image or click to upload"}
                </p>
                <input ref={fileRef} type="file" accept="image/*" onChange={(e) => loadFile(e.target.files[0])} style={{ display: "none" }} />
              </div>
            </div>

            {/* Headline */}
            <div>
              <Label text="Headline" />
              <textarea
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                rows={2}
                style={{ width: "100%", background: "#071220", border: "1px solid #1E2D3D", borderRadius: 4, color: "#F5F0E8", fontSize: 13, padding: "7px 10px", fontFamily: font, letterSpacing: "0.05em", resize: "vertical", outline: "none", marginBottom: 6 }}
              />
              <p style={{ margin: "0 0 8px", fontSize: 10, color: "#4A6070", fontFamily: "sans-serif", lineHeight: 1.5 }}>
                Wrap a word in <span style={{ color: highlightColor }}>*asterisks*</span> to highlight it — e.g. <code style={{ color: "#8BA0AF" }}>SHE ALREADY *LEFT*</code>
              </p>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: "#6B7F8E", whiteSpace: "nowrap", minWidth: 70 }}>Size: {hSize}px</span>
                <input type="range" min={22} max={76} value={hSize} onChange={(e) => setHSize(+e.target.value)} style={{ flex: 1, accentColor: "#6B7F8E" }} />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "#6B7F8E", whiteSpace: "nowrap", minWidth: 70 }}>Lines: {lineSpacing.toFixed(2)}×</span>
                <input type="range" min={1} max={1.6} step={0.02} value={lineSpacing} onChange={(e) => setLineSpacing(+e.target.value)} style={{ flex: 1, accentColor: "#6B7F8E" }} />
              </div>
            </div>

            {/* Subline */}
            <div>
              <Label text="Subline" />
              <input
                type="text" value={sub} onChange={(e) => setSub(e.target.value)}
                placeholder="e.g. The Silent Divorce"
                style={{ width: "100%", background: "#071220", border: "1px solid #1E2D3D", borderRadius: 4, color: "#F5F0E8", fontSize: 13, padding: "7px 10px", fontStyle: "italic", outline: "none" }}
              />
            </div>

            {/* Channel */}
            <div>
              <Label text="Channel" />
              <input
                type="text" value={channel} onChange={(e) => setChannel(e.target.value)}
                style={{ width: "100%", background: "#071220", border: "1px solid #1E2D3D", borderRadius: 4, color: "#F5F0E8", fontSize: 13, padding: "7px 10px", outline: "none" }}
              />
            </div>

            {/* Headline Color */}
            <div>
              <Label text="Headline Color" />
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                {HEADLINE_COLORS.map((c) => (
                  <Swatch key={c.value} color={c.value} active={hColor === c.value} onClick={() => setHColor(c.value)} />
                ))}
                <input type="color" value={hColor} onChange={(e) => setHColor(e.target.value)}
                  style={{ width: 28, height: 28, border: "1px solid #2A3D52", borderRadius: 4, cursor: "pointer", padding: 1 }} />
              </div>
            </div>

            {/* Highlight Color */}
            <div>
              <Label text="Highlight Color (for *words*)" />
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                {HEADLINE_COLORS.map((c) => (
                  <Swatch key={c.value} color={c.value} active={highlightColor === c.value} onClick={() => setHighlightColor(c.value)} />
                ))}
                <input type="color" value={highlightColor} onChange={(e) => setHighlightColor(e.target.value)}
                  style={{ width: 28, height: 28, border: "1px solid #2A3D52", borderRadius: 4, cursor: "pointer", padding: 1 }} />
              </div>
            </div>

            {/* Font */}
            <div>
              <Label text="Font" />
              <select
                value={font} onChange={(e) => setFont(e.target.value)}
                style={{ width: "100%", background: "#071220", border: "1px solid #1E2D3D", borderRadius: 4, color: "#F5F0E8", fontSize: 13, padding: "7px 10px", fontFamily: "sans-serif", cursor: "pointer", outline: "none" }}
              >
                {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>

            {/* Text Position */}
            <div>
              <Label text="Text Position" />
              <div style={{ display: "flex", gap: 6 }}>
                {["right", "left", "center"].map((p) => (
                  <PillBtn key={p} active={pos === p} onClick={() => setPos(p)}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </PillBtn>
                ))}
              </div>
            </div>

            {/* Overlay */}
            <div>
              <Label text="Overlay" />
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
                {OVERLAYS.map((o) => (
                  <PillBtn key={o.label} active={overlayDir === o.dir} onClick={() => setOverlayDir(o.dir)} small>
                    {o.label}
                  </PillBtn>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "#6B7F8E", whiteSpace: "nowrap" }}>{opacity}%</span>
                <input type="range" min={0} max={90} step={5} value={opacity} onChange={(e) => setOpacity(+e.target.value)} style={{ flex: 1, accentColor: "#6B7F8E" }} />
              </div>
            </div>

            {/* Brand Colors */}
            <div>
              <Label text="Brand Colors" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <p style={{ fontSize: 11, color: "#6B7F8E", marginBottom: 4, fontFamily: "sans-serif" }}>Background</p>
                  <input type="color" value={bg} onChange={(e) => setBg(e.target.value)}
                    style={{ width: "100%", height: 30, border: "1px solid #2A3D52", borderRadius: 4, cursor: "pointer", padding: 2 }} />
                </div>
                <div>
                  <p style={{ fontSize: 11, color: "#6B7F8E", marginBottom: 4, fontFamily: "sans-serif" }}>Accent</p>
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)}
                    style={{ width: "100%", height: 30, border: "1px solid #2A3D52", borderRadius: 4, cursor: "pointer", padding: 2 }} />
                </div>
              </div>
            </div>

            {/* Elements */}
            <div>
              <Label text="Elements" />
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#C8C4BC" }}>
                <input type="checkbox" checked={showLogo} onChange={(e) => setShowLogo(e.target.checked)} style={{ accentColor: "#6B7F8E", width: 14, height: 14 }} />
                Show compass logo mark
              </label>
            </div>

          </div>

          {/* Preview + Download Panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

            {/* Toolbar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#4A6070", letterSpacing: "0.15em", textTransform: "uppercase" }}>
                Preview — 1280 × 720
              </span>

              {/* Download Button */}
              <button
                onClick={handleDownload}
                disabled={downloading}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 22px", fontSize: 13, fontFamily: "Georgia, serif",
                  fontWeight: 700, letterSpacing: "0.06em",
                  background: downloading ? "#1E2D3D" : "#6B7F8E",
                  border: "none", borderRadius: 6,
                  color: downloading ? "#4A6070" : "#0A0F1A",
                  cursor: downloading ? "not-allowed" : "pointer",
                  transition: "background 0.2s",
                }}
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none"
                  stroke={downloading ? "#4A6070" : "#0A0F1A"}
                  strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {downloading ? "Rendering…" : "Download 1280×720 PNG"}
              </button>
            </div>

            {/* Preview Canvas */}
            <div style={{
              position: "relative", width: "100%", aspectRatio: "16/9",
              background: bg, borderRadius: 8, overflow: "hidden",
              border: "1px solid #1E2D3D", boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
            }}>
              {img && (
                <img src={img} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }} />
              )}
              <div style={{ position: "absolute", inset: 0, background: gradMap[overlayDir], zIndex: 2, pointerEvents: "none" }} />

              <div style={textContainerStyle}>
                <div style={{
                  fontFamily: font, fontSize: hSize, fontWeight: 700,
                  color: hColor, lineHeight: lineSpacing, letterSpacing: "0.04em",
                  textTransform: "uppercase", marginBottom: 10,
                  textShadow: "2px 2px 14px rgba(0,0,0,0.7)", wordBreak: "break-word",
                }}>
                  {previewTokens.length === 0 ? "YOUR HEADLINE" : previewTokens.map((tok, i) => (
                    <span key={i} style={{ fontFamily: "inherit", color: tok.highlight ? highlightColor : "inherit" }}>
                      {i > 0 ? " " : ""}{tok.text}
                    </span>
                  ))}
                </div>
                <div style={accentBarStyle} />
                {sub && (
                  <div style={{ fontFamily: font, fontSize: Math.round(hSize * 0.3), fontStyle: "italic", color: "#C8C4BC", letterSpacing: "0.1em", marginBottom: 7, textShadow: "1px 1px 6px rgba(0,0,0,0.6)" }}>
                    {sub}
                  </div>
                )}
                <div style={{ fontFamily: font, fontSize: Math.round(hSize * 0.2), color: "#8BA0AF", letterSpacing: "0.22em", textTransform: "uppercase", fontStyle: "italic", textShadow: "1px 1px 4px rgba(0,0,0,0.5)" }}>
                  {channel}
                </div>
              </div>

              {showLogo && (
                <div style={{ position: "absolute", bottom: 10, left: 12, zIndex: 10, opacity: 0.65 }}>
                  <CompassMark color={accent} size={18} />
                </div>
              )}

              {!img && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
                  <div style={{ border: "1px dashed #1E2D3D", borderRadius: 6, padding: "10px 18px" }}>
                    <p style={{ fontSize: 12, color: "#2A3D52", margin: 0 }}>Upload your oil painting to preview</p>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Presets */}
            <div style={{ background: "#0F1C2E", border: "1px solid #1E2D3D", borderRadius: 6, padding: "1rem 1.25rem" }}>
              <Label text="Quick Presets — All 3 Scripts" />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => { setHeadline(p.headline); setSub(p.sub); }}
                    style={{ padding: "6px 12px", fontSize: 12, background: "#071220", border: "1px solid #2A3D52", borderRadius: 4, color: "#8BA0AF", cursor: "pointer", fontFamily: "Georgia, serif" }}
                  >
                    {p.label}: {p.headline}
                  </button>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div style={{ background: "#0F1C2E", border: "1px solid #1E2D3D", borderRadius: 6, padding: "1rem 1.25rem" }}>
              <Label text="How to use" />
              <ol style={{ paddingLeft: "1.2rem", fontSize: 12, color: "#6B7F8E", lineHeight: 2.1, margin: 0 }}>
                <li>Generate your oil painting in ChatGPT and save it to your device</li>
                <li>Drop it into the upload zone — it loads instantly into the preview</li>
                <li>Edit headline — wrap key words in <span style={{ color: highlightColor }}>*asterisks*</span> to highlight them</li>
                <li>Use the <strong style={{ color: "#8BA0AF" }}>Lines</strong> slider to widen line spacing on wrapped headlines</li>
                <li><strong style={{ color: "#8BA0AF" }}>Click Download 1280×720 PNG</strong> — file saves directly to your device</li>
                <li>Upload the PNG straight to YouTube Studio as your video thumbnail</li>
              </ol>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}